import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { FaComments, FaPaperPlane, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getOrCreateChatIdentity } from "../../utils/chatSession";
import { trapFocus } from "../../utils/accessibility";

const WEBHOOK_URL = "https://n8nsiyam.siyamuddin.xyz/webhook/chat-agent";

const ChatLauncher = styled.button`
  position: fixed;
  bottom: 30px;
  right: 95px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  z-index: 1001;
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  background: ${({ theme }) => theme.primary};
  box-shadow: 0 10px 30px rgba(133, 76, 230, 0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(133, 76, 230, 0.52);
  }
`;

const ChatPanel = styled.section`
  position: fixed;
  right: 30px;
  bottom: ${({ $bottom }) => $bottom || "100px"};
  width: min(380px, calc(100vw - 30px));
  height: 520px;
  max-height: calc(100vh - 130px);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  background: ${({ theme }) => theme.card};
  display: flex;
  flex-direction: column;
  z-index: 1001;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    right: 12px;
    bottom: 88px;
    width: calc(100vw - 24px);
    height: min(500px, calc(100vh - 120px));
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => `${theme.primary}2a`};
  color: ${({ theme }) => theme.text_primary};
  background: ${({ theme }) => `${theme.primary}20`};
  font-weight: 600;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text_primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => `${theme.primary}22`};
  }
`;

const MessageList = styled.div`
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bubble = styled.div`
  max-width: 85%;
  align-self: ${({ $variant }) =>
    $variant === "user" ? "flex-end" : "flex-start"};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  color: ${({ theme, $variant }) =>
    $variant === "user" ? "#fff" : theme.text_primary};
  background: ${({ theme, $variant }) =>
    $variant === "user" ? theme.primary : `${theme.primary}1a`};
`;

const Starters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 6px 0 4px;
`;

const StarterButton = styled.button`
  border: 1px solid ${({ theme }) => `${theme.primary}45`};
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  border-radius: 999px;
  font-size: 12px;
  padding: 6px 10px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }
`;

const ErrorText = styled.div`
  color: #ff7675;
  font-size: 12px;
  padding: 0 12px 8px;
`;

const Composer = styled.form`
  display: flex;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => `${theme.primary}2a`};
  padding: 10px;
`;

const Input = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 42px;
  max-height: 110px;
  border-radius: 10px;
  padding: 10px;
  border: 1px solid ${({ theme }) => `${theme.primary}40`};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
  font-family: inherit;
  font-size: 14px;
`;

const SendButton = styled.button`
  width: 44px;
  min-width: 44px;
  height: 42px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  color: #fff;
  background: ${({ theme, disabled }) =>
    disabled ? `${theme.primary}66` : theme.primary};
`;

const getReplyText = (payload) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  if (typeof payload.output === "string") return payload.output;
  if (Array.isArray(payload) && payload[0]) return getReplyText(payload[0]);
  if (payload.data) return getReplyText(payload.data);
  if (payload.response) return getReplyText(payload.response);
  return "";
};

const FloatingChat = ({
  hideLauncher = false,
  isOpen: controlledOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = useCallback((value) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  }, [isControlled, onOpenChange]);

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      messageType: "assistant",
      text: t("chat.welcome"),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const identity = useMemo(() => getOrCreateChatIdentity(), []);
  const messageEndRef = useRef(null);
  const panelRef = useRef(null);

  const starterPrompts = t("chat.starters", { returnObjects: true });

  useEffect(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === "welcome" ? { ...m, text: t("chat.welcome") } : m
      )
    );
  }, [t]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return undefined;

    const releaseFocus = trapFocus(panelRef.current);
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);

    return () => {
      releaseFocus();
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, setIsOpen]);

  const sendMessage = async (rawMessage) => {
    const message = rawMessage.trim();
    if (!message || isLoading) return;

    const userMessage = {
      id: `u-${Date.now()}`,
      messageType: "user",
      text: message,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          sessionId: identity.sessionId,
          userId: identity.userId,
          metadata: { source: "portfolio-chat" },
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      } else {
        payload = await response.text();
      }

      if (!response.ok) {
        throw new Error(
          typeof payload === "string"
            ? payload
            : "Request failed. Please try again."
        );
      }

      const reply =
        getReplyText(payload) || t("chat.parseError");
      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, messageType: "assistant", text: reply },
      ]);
    } catch (err) {
      setError(t("chat.error"));
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          messageType: "assistant",
          text: t("chat.offline"),
        },
      ]);
      // eslint-disable-next-line no-console
      console.error("FloatingChat request failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {isOpen && (
        <ChatPanel
          ref={panelRef}
          aria-label={t("chat.panelLabel")}
          tabIndex={-1}
        >
          <ChatHeader>
            {t("chat.title")}
            <IconButton
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label={t("chat.close")}
            >
              <FaTimes />
            </IconButton>
          </ChatHeader>

          <MessageList role="log" aria-live="polite" aria-relevant="additions">
            {messages.map((message) => (
              <Bubble key={message.id} $variant={message.messageType}>
                {message.text}
              </Bubble>
            ))}

            {messages.length <= 1 && Array.isArray(starterPrompts) && (
              <Starters>
                {starterPrompts.map((prompt) => (
                  <StarterButton
                    key={prompt}
                    type="button"
                    onClick={() => sendMessage(prompt)}
                    disabled={isLoading}
                  >
                    {prompt}
                  </StarterButton>
                ))}
              </Starters>
            )}

            {isLoading && (
              <Bubble $variant="assistant">{t("chat.typing")}</Bubble>
            )}
            <div ref={messageEndRef} />
          </MessageList>

          {error && <ErrorText role="alert">{error}</ErrorText>}

          <Composer onSubmit={onSubmit}>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              aria-label={t("chat.inputLabel")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit(e);
                }
              }}
            />
            <SendButton
              type="submit"
              disabled={isLoading || !input.trim()}
              aria-label={t("chat.send")}
            >
              <FaPaperPlane />
            </SendButton>
          </Composer>
        </ChatPanel>
      )}

      {!hideLauncher && (
        <ChatLauncher
          type="button"
          $hidden={false}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? t("chat.close") : t("chat.open")}
        >
          {isOpen ? <FaTimes /> : <FaComments />}
        </ChatLauncher>
      )}
    </>
  );
};

export default FloatingChat;
